import { Field, Form, Formik, useField } from 'formik';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import AutocompleteV2 from '../../../../component/AutocompleteObjectV2';
import { IconButtonSave } from '../../../../component/IconSvg';
import LabelRequired from '../../../../component/LabelRequired';
import SimpleTable from '../../../../component/table/simple-table/SimpleTable';
import SearchField from '../../../components/tab-kham-benh/modal-chon-ket-qua-dich-vu/components/SearchField';
import { RowMouseEventHandlerParams } from 'react-virtualized';
import { formatDateTime } from '../../../../utils/FormatUtils';
import { APP_TIMESTAMP_FORMAT } from '../../../../utils/Constant';
import DatePickerCustom from 'src/app/modules/component/date-picker/DatePickerCustom';
import { getTreatmentSheetsGroupedByDate } from 'src/app/modules/phan-he-noi-tru-dieu-tri/services/DieuTriServices';

type ModalProps = {
	open: boolean;
	onClose: () => void;
	onSave: (value: DateType[]) => void;
	treatmentId?: string;
};

type DateType = {
	name: string;
	value: string;
	dayOfWeek: string;
	shortName: string;
};

type TimeType = {
	value: string;
	name: string;
};

type Props = {
	name: string;
	treatmentId?: string;
};

type TreatmentSheetItem = {
	id: string;
	patientId: string;
	visitId: string;
	medicalRecordCode: string;
	treatmentId: string;
	treatmentType: string | null;
	createdDate: string;
	prescriptionDate: string;
	values: any;
};

type TreatmentSheetResponse = {
	[date: string]: TreatmentSheetItem[];
};

const NgayKeTuDong = ({ name, treatmentId }: Props) => {
	const [openSelectDialog, setOpenSelectDialog] = useState(false);
	const [value, setValue] = useState<string>('');

	const [field, meta, helper] = useField(name);

	useEffect(() => {
		if (field.value) {
			setValue(
				Array.isArray(field.value)
					? field.value
						.map((item: any) => item?.shortName)
						.join(', ')
						.trim()
					: field.value
						.split(',')
						.map((item: string) => moment(new Date(item)).format('DD/MM'))
						.join(', ')
						.trim()
			);
		}
	}, [field.value]);

	const onSelect = (selectedDate: DateType[]) => {
		const dataArraySorted = sortDate(selectedDate);

		setValue(
			dataArraySorted
				.map((item: DateType) => item.shortName)
				.join(', ')
				.trim()
		);
		let submitValue = treatmentId ? dataArraySorted : dataArraySorted.map((item: DateType) => item.value).join(',');
		helper.setValue(submitValue);
		setOpenSelectDialog(false);
	};

	const sortDate = (dateArray: any[]) => {
		return dateArray
			.sort((a, b) => new Date(a.value).getTime() - new Date(b.value).getTime())
			.map(row => row);
	};

	return (
		<div className="d-flex align-items-center">
			<Field as="input" hidden {...field} />
			<LabelRequired label="Kê tự động cho các ngày" className="spaces min-w-165" />
			<Button
				onClick={() => {
					setOpenSelectDialog(true);
				}}
				className="link-info text-decoration-underline"
				variant='link'
			>
				<em>
					<i className="bi bi-calendar4-week m-0 px-0 pe-2"></i>
					<span>{!value ? 'Chọn ngày kê tự động' : value}</span>
				</em>
			</Button>
			{openSelectDialog && (
				<ModalChonNgay
					open={openSelectDialog}
					onClose={() => {
						setOpenSelectDialog(false);
					}}
					onSave={onSelect}
					treatmentId={treatmentId}
				/>
			)}
		</div>
	);
};

const ModalChonNgay = ({ open, onClose, onSave, treatmentId }: ModalProps) => {
	const initialDataRef = useRef<DateType[]>([]);

	const [tableData, setTableData] = useState<DateType[]>([]);
	const [value, setValue] = useState<any[]>([]);
	const [timeOptions, setTimeOptions] = useState<TimeType[]>([]);
	const [timeValue, setTimeValue] = useState('');
	const [fromDate, setFromDate] = useState<any>(moment().startOf('week').add(1, "day").format(APP_TIMESTAMP_FORMAT));
	const [toDate, setToDate] = useState<any>(moment().add(2, "week").format(APP_TIMESTAMP_FORMAT));
	const [treatmentSheetData, setTreatmentSheetData] = useState<TreatmentSheetResponse>({});

	const fetchTreatmentSheets = async () => {
		if (!treatmentId) return;
		try {
			const response = await getTreatmentSheetsGroupedByDate({ treatmentId });
			setTreatmentSheetData(response?.data?.data || {});
		} catch (error) {
			console.error('Error fetching treatment sheets:', error);
		}
	};

	useEffect(() => {
		if (treatmentId) {
			fetchTreatmentSheets();
		}
	}, [treatmentId]);

	useEffect(() => {
		generateData(fromDate, toDate);
	}, [fromDate, toDate, treatmentSheetData]);

	const generateData = (fromDate: any, toDate: any) => {
		let _fromDate = moment(fromDate);
		let _toDate = moment(toDate);

		const countDayDiff = moment(_toDate).diff(_fromDate, 'day');
		const DAY_OF_WEEK = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
		const dateOptions: any[] = [];
		const timeOptions: TimeType[] = [];

		for (let index = 0; index <= countDayDiff; index++) {
			const currentDate = moment(_fromDate).add(index, 'day');
			const dateKey = currentDate.format('YYYY-MM-DD');
			// Lấy item đầu tiên từ response theo ngày làm giá trị mặc định
			const defaultTreatmentSheet = treatmentSheetData[dateKey]?.[0] || null;

			dateOptions.push({
				name: `Ngày ${currentDate.format('DD/MM/YYYY')}`,
				value: formatDateTime(currentDate.toString(), APP_TIMESTAMP_FORMAT)!,
				dayOfWeek: DAY_OF_WEEK[currentDate.day()],
				shortName: currentDate.format('DD/MM'),
				treatmentSheetId: defaultTreatmentSheet?.id || null,
				viewValue: defaultTreatmentSheet ? formatDateTime(defaultTreatmentSheet.prescriptionDate) : null,
			});
		}
		initialDataRef.current = dateOptions;
		setTableData(dateOptions);
		setValue([]);

		for (let hour = 0; hour < 24; hour++) {
			for (let minute = 0; minute < 60; minute += 5) {
				const option = `${hour < 10 ? `0${hour}` : hour}:${
					minute < 10 ? `0${minute}` : minute
				}`;
				timeOptions.push({
					name: option,
					value: option,
				});
			}
		}
		setTimeOptions(timeOptions);
	};

	const onSearch = (key: string) => {};

	const columns = useMemo(
		() => [
			{
				name: '',
				field: 'checkbox',
				width: 50,
				textAlign: 'center',
			},
			{
				name: 'Ngày',
				field: 'name',
				width: 300,
			},
			{
				name: 'Thứ',
				field: 'dayOfWeek',
				width: 100,
			},
			...(treatmentId ? [{
				name: 'Phiếu điều trị',
				field: 'treatmentSheet',
				width: 200,
				cellRenderer: ({ rowIndex, rowData }: any) => {
					// Lấy ngày từ value của row (format: YYYY-MM-DDTHH:mm:ss) -> YYYY-MM-DD
					const rowDateKey = rowData?.value ? moment(rowData.value).format('YYYY-MM-DD') : '';
					// Lấy danh sách phiếu điều trị theo ngày tương ứng
					const treatmentOptions = treatmentSheetData[rowDateKey] || [];

					return rowData?.isChecked ? (
						<div onClick={(e) => e.stopPropagation()}>
							<Formik initialValues={{}} onSubmit={() => { }} >
								<AutocompleteV2
									options={treatmentOptions}
									getOptionLabel={option => formatDateTime(option?.prescriptionDate) as string}
									value={treatmentOptions.find(item => item.id === rowData.treatmentSheetId) || null}
									name="treatmentSheetId"
									onChange={selectedOption => {
										setTableData(prev => prev.map((row) => (
											row?.shortName === rowData?.shortName ? {
												...row,
												treatmentSheetId: selectedOption?.id || null,
												viewValue: formatDateTime(selectedOption?.prescriptionDate)
											} : row
										)));
										setValue(prev => prev.map((row) => (
											row.shortName === rowData?.shortName ? {
												...row,
												treatmentSheetId: selectedOption?.id || null
											} : row
										)));
									}}
									className="autocomplete-custom-tiep-nhan radius spaces h-29"
								/>
							</Formik>
						</div>
					) : (rowData.treatmentSheetId && rowData.viewValue ? rowData.viewValue : "");
				},
			}] : []),
		],
		[treatmentId, treatmentSheetData]
	);

	const onRenderCell = (columnData: any, dataKey: string, rowData: any) => {
		return rowData[dataKey];
	};

	const onSubmit = () => {
		const [hour, minute] = timeValue.split(':');

		const result = value.map((record: DateType) => ({
			...record,
			value: moment(record.value)
				.set({
					hour: Number(hour),
					minute: Number(minute),
				})
				.format(APP_TIMESTAMP_FORMAT),
		}));
		onSave(result);
	};

	return (
		<Modal size="lg" show={open} animation centered className="dialog-background">
			<Modal.Header className="py-3 header-modal">
				<Modal.Title className="text-pri">Chọn ngày</Modal.Title>
				<Button className="btn-close bg-white" onClick={onClose}></Button>
			</Modal.Header>

			<Modal.Body className="py-16 px-24 spaces">
				<div className="d-flex align-items-center mb-3">
					<div className="d-flex align-items-center spaces pr-12">
						<LabelRequired className="min-w-65px fw-bold" label="Từ ngày" />
						<DatePickerCustom
							name='fromDate'
							value={fromDate || ""}
							dateFormatOutput={APP_TIMESTAMP_FORMAT}
							setDateValue={(date: string | null) => setFromDate(date)}
						/>
					</div>
					<div className="d-flex align-items-center spaces px-12">
						<LabelRequired className="min-w-70px fw-bold" label="Đến ngày" />
						<DatePickerCustom
							name='toDate'
							value={toDate || ""}
							dateFormatOutput={APP_TIMESTAMP_FORMAT}
							setDateValue={(date: string | null) => setToDate(date)}
						/>
					</div>
				</div>
				<SearchField onSearch={onSearch} />
				<SimpleTable
					data={tableData}
					columns={columns}
					height={400}
					width={1000}
					scrollable={true}
					setDataChecked={setValue}
					dataChecked={value}
					uniquePrefix="value"
					handleRenderCell={onRenderCell}
					onRowClick={({ rowData }: RowMouseEventHandlerParams) => {
						let selected = [...value];
						const rowExist = selected.some((row: any) => row.value === rowData.value);
						if (rowExist) {
							selected = selected.filter((row: any) => row.value !== rowData.value);
						} else {
							selected.push(rowData);
						}
						setValue(selected);
					}}
				/>
			</Modal.Body>

			<Modal.Footer className="d-flex justify-content-between py-1">
				<div className="d-flex">
					<LabelRequired label="Thời gian chỉ định" className="spaces min-w-150" />
					<div className="w-100px">
						<Formik initialValues={{}} onSubmit={() => {}}>
							<AutocompleteV2
								options={timeOptions}
								name="thoiGianChiDinh"
								className="autocomplete-custom-tiep-nhan radius spaces h-26"
								value={timeOptions.find(item => item.value === timeValue)}
								onChange={option => {
									setTimeValue(option.value);
								}}
							/>
						</Formik>
					</div>
				</div>
				<Button className="btn-fill" onClick={onSubmit}>
					<IconButtonSave />
					<span>Lưu</span>
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export { ModalChonNgay };
export default NgayKeTuDong;
